import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, JSX, Listen, Prop, Watch } from '@stencil/core';
import { transformTagNames } from '../../global/transform-tag-name';
import { SharedScrollLockService } from '../../global/workbench-global';
import { Keys } from '../../shared/keys';
import { throttle } from '../../utils/throttle';
import { getRandomId } from '../../utils/get-random-id';
import { getDataOnFromTheme, WorkbenchTheme } from '../../shared/themes';

const SCROLL_THROTTLE_MS = 100;

/**
 * @slot - Contents placed within the fly-in
 * @slot header-button - Primary call to action placed within the fly-in header
 * @slot headline - Headline for the sticky bar
 * @slot subline - Subline for the sticky bar
 * @slot layer-headline - Headline of the fly-in
 */
@Component({
  tag: 'wb-subnavigation-fly-in',
  styleUrl: 'subnavigation-fly-in.scss',
  shadow: true,
})
export class SubnavigationFlyInComponent implements ComponentInterface {
  @Element()
  public el!: HTMLElement;

  /** colour scheme */
  @Prop({ reflect: true })
  public theme: WorkbenchTheme;

  /** set to true to make component fly in */
  @Prop({ reflect: true, mutable: true })
  public open: boolean = false;

  /** If present, fly-in closes on escape key */
  @Prop()
  public closeOnEscapeKey?: boolean = false;

  /** aria label for trigger that opens the fly in */
  @Prop()
  public openAriaLabel: string;

  /** aria label for trigger that closes the fly in */
  @Prop()
  public closeAriaLabel: string;

  /** Event emitted when internally rendered wb-fly-in opens */
  @Event()
  public wbsubnavigationflyinopen: EventEmitter<void>;

  /**
   * @deprecated Use `wbsubnavigationflyinopen` instead
   * Event emitted when internally rendered wb-fly-in opens
   */
  @Event()
  public wbopen: EventEmitter<void>;

  /** Event emitted when internally rendered wb-fly-in closes */
  @Event({ bubbles: true })
  public wbsubnavigationflyinclose!: EventEmitter<null>;

  /**
   * @deprecated Use `wbsubnavigationflyinclose` instead
   * Event emitted when internally rendered wb-fly-in closes
   */
  @Event({ bubbles: true })
  public wbclose!: EventEmitter<null>;

  private TagNames = transformTagNames(this, 'wb-subnavigation-fly-in', ['wb-subnavigation-fly-in-item', 'wb-icon', 'wb-text', 'wb-fly-in', 'wb-round-button']);

  private flyInEl: HTMLWbFlyInElement;
  private flyInHeaderEl: HTMLDivElement;
  private flyInInnerEl: HTMLDivElement;
  private uniqueId = getRandomId();

  @Watch('open')
  public onOpenChange(): void {
    this.updateState();
  }

  @Listen('keyup', { target: 'window' })
  public onKeyUp(e: KeyboardEvent): void {
    if (this.closeOnEscapeKey && e.key === Keys.ESC) {
      this.close();
    }
  }

  private close = (): void => {
    this.open = false;
  };

  private toggle = (): void => {
    this.open = !this.open;
  };

  private updateState(): void {
    this.handleScrollLock();
    this.handleFlyIn();
  }

  private handleScrollLock(): void {
    // TODO: Move scroll lock to wb-fly-in component (potentially as optional property)
    this.open //
      ? SharedScrollLockService.lock(this.uniqueId)
      : SharedScrollLockService.unlock(this.uniqueId);
  }

  private handleFlyIn(): void {
    this.flyInEl.open = this.open;
    if (this.open) {
      this.wbsubnavigationflyinopen.emit();
      this.wbopen.emit();
    }
  }

  private handleFlyInInnerScroll = (): void => {
    const isScrolled = this.flyInInnerEl.scrollTop > 0;
    this.flyInHeaderEl.classList.toggle('has-shadow', isScrolled);
  };

  private handleFlyInClose = (event: Event): void => {
    event.stopPropagation(); // stop propagation of initial event thrown by the internally rendered wb-fly-in
    this.wbsubnavigationflyinclose.emit();
    this.wbclose.emit();
  };

  private throttledHandleFlyInInnerScroll = throttle(this.handleFlyInInnerScroll, SCROLL_THROTTLE_MS);

  private setFlyInElRef = (ref) => (this.flyInEl = ref);
  private setFlyInHeaderElRef = (ref) => (this.flyInHeaderEl = ref);
  private setFlyInInnerElRef = (ref) => (this.flyInInnerEl = ref);

  public render(): JSX.Element {
    const { WbIcon, WbText, WbFlyIn, WbRoundButton } = this.TagNames;

    return (
      <Host class="wb-subnavigation-fly-in" data-on={getDataOnFromTheme(this.el, this.theme)}>
        <div class="header has-shadow" onClick={this.toggle}>
          <div class="header-inner">
            <div class="start">
              <WbText size="l" strong ellipsis>
                <slot name="headline" />
              </WbText>
              <WbText size="m" ellipsis>
                <slot name="subline" />
              </WbText>
            </div>
            <div class="end">
              <WbRoundButton size="new-l" button-aria-label={this.openAriaLabel}>
                <WbIcon name="chevron-down/24" />
                {this.openAriaLabel}
              </WbRoundButton>
              <slot name="header-button" />
            </div>
          </div>
        </div>

        <WbFlyIn from="top" ref={this.setFlyInElRef} onWbflyinclose={this.handleFlyInClose}>
          <div class="header" onClick={this.toggle} ref={this.setFlyInHeaderElRef}>
            <div class="header-inner">
              <WbText class="start" size="l" strong>
                <slot name="layer-headline" />
              </WbText>
              <div class="end">
                <WbRoundButton size="new-l" button-aria-label={this.closeAriaLabel}>
                  <WbIcon name="close/24" />
                  {this.closeAriaLabel}
                </WbRoundButton>
              </div>
            </div>
          </div>

          <div class="inner" onScroll={this.throttledHandleFlyInInnerScroll} ref={this.setFlyInInnerElRef}>
            <slot />
          </div>
        </WbFlyIn>
      </Host>
    );
  }
}
